/**
 * MenuItemManager.ts - Menu Item Management Utility
 * 
 * Utility class for managing menu items (filtering, sorting, grouping)
 * Migrated from Interfacr
 * 
 * CHANGE HISTORY:
 * - 251115_1640 [current] danielbeer: Migrated from Interfacr
 */

import type { MenuItem } from './types';

export class MenuItemManager {
  private items: MenuItem[];
  
  constructor(items: MenuItem[]) {
    this.items = items;
  }
  
  /**
   * Get all items
   */
  getAll(): MenuItem[] {
    return this.items;
  }
  
  /**
   * Get visible items (not hidden)
   */
  getVisibleItems(): MenuItem[] {
    return this.items.filter(item => !item.hidden);
  }
  
  /**
   * Get item by ID
   */
  getById(id: string): MenuItem | undefined {
    return this.findItemRecursive(this.items, id);
  }
  
  /**
   * Find item recursively (including children)
   */
  private findItemRecursive(items: MenuItem[], id: string): MenuItem | undefined {
    for (const item of items) {
      if (item.id === id) {
        return item;
      }
      if (item.children) {
        const found = this.findItemRecursive(item.children, id);
        if (found) return found;
      }
    }
    return undefined;
  }
  
  /**
   * Get items by category
   */
  getByCategory(category: string): MenuItem[] {
    return this.items.filter(item => item.category === category);
  }
  
  /**
   * Get all categories
   */
  getCategories(): string[] {
    const categories = new Set<string>();
    this.items.forEach(item => {
      if (item.category) {
        categories.add(item.category);
      }
    });
    return Array.from(categories).sort();
  }
  
  /**
   * Get items by type
   */
  getByType(type: MenuItem['type']): MenuItem[] {
    return this.items.filter(item => item.type === type);
  }
  
  /**
   * Search items by label
   */
  search(query: string): MenuItem[] {
    const lowerQuery = query.toLowerCase();
    return this.searchRecursive(this.items, lowerQuery);
  }
  
  /**
   * Search items recursively
   */
  private searchRecursive(items: MenuItem[], query: string): MenuItem[] {
    const results: MenuItem[] = [];
    
    for (const item of items) {
      if (item.label.toLowerCase().includes(query)) {
        results.push(item);
      }
      if (item.children) {
        results.push(...this.searchRecursive(item.children, query));
      }
    }
    
    return results;
  }
  
  /**
   * Sort items by order
   */
  sortByOrder(): MenuItem[] {
    return [...this.items].sort((a, b) => {
      const orderA = a.order ?? 999;
      const orderB = b.order ?? 999;
      return orderA - orderB;
    });
  }
  
  /**
   * Sort items by label
   */
  sortByLabel(): MenuItem[] {
    return [...this.items].sort((a, b) => a.label.localeCompare(b.label));
  }
  
  /**
   * Sort items by custom order
   */
  sortByCustomOrder(customOrder: string[]): MenuItem[] {
    const orderMap = new Map(customOrder.map((id, index) => [id, index]));
    
    return [...this.items].sort((a, b) => {
      const orderA = orderMap.get(a.id) ?? 999;
      const orderB = orderMap.get(b.id) ?? 999;
      return orderA - orderB;
    });
  }
  
  /**
   * Filter items
   */
  filter(predicate: (item: MenuItem) => boolean): MenuItem[] {
    return this.items.filter(predicate);
  }
  
  /**
   * Group items by category
   */
  groupByCategory(): Record<string, MenuItem[]> {
    const groups: Record<string, MenuItem[]> = {};
    
    this.items.forEach(item => {
      const category = item.category || 'Uncategorized';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(item);
    });
    
    return groups;
  }
  
  /**
   * Get item count
   */
  getCount(): number {
    return this.items.length;
  }
  
  /**
   * Get visible item count
   */
  getVisibleCount(): number {
    return this.getVisibleItems().length;
  }
  
  /**
   * Check if item exists
   */
  has(id: string): boolean {
    return this.getById(id) !== undefined;
  }
  
  /**
   * Get parent item
   */
  getParent(itemId: string): MenuItem | undefined {
    return this.findParentRecursive(this.items, itemId);
  }
  
  /**
   * Find parent recursively
   */
  private findParentRecursive(items: MenuItem[], targetId: string): MenuItem | undefined {
    for (const item of items) {
      if (item.children) {
        if (item.children.some(child => child.id === targetId)) {
          return item;
        }
        const found = this.findParentRecursive(item.children, targetId);
        if (found) return found;
      }
    }
    return undefined;
  }
  
  /**
   * Get item path (breadcrumb)
   */
  getPath(itemId: string): MenuItem[] {
    const path: MenuItem[] = [];
    this.buildPathRecursive(this.items, itemId, path);
    return path;
  }
  
  /**
   * Build path recursively
   */
  private buildPathRecursive(items: MenuItem[], targetId: string, path: MenuItem[]): boolean {
    for (const item of items) {
      path.push(item);
      
      if (item.id === targetId) {
        return true;
      }
      
      if (item.children && this.buildPathRecursive(item.children, targetId, path)) {
        return true;
      }
      
      path.pop();
    }
    return false;
  }
  
  /**
   * Get all item IDs
   */
  getAllIds(): string[] {
    return this.getAllIdsRecursive(this.items);
  }
  
  /**
   * Get all IDs recursively
   */
  private getAllIdsRecursive(items: MenuItem[]): string[] {
    const ids: string[] = [];
    
    for (const item of items) {
      ids.push(item.id);
      if (item.children) {
        ids.push(...this.getAllIdsRecursive(item.children));
      }
    }
    
    return ids;
  }
}
